import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Facebook, Twitter, Linkedin, Instagram } from 'lucide-react';

export const Footer = () => {
    const currentYear = new Date().getFullYear();

    const quickLinks = [
        { name: 'Home', path: '/' },
        { name: 'About Us', path: '/about' },
        { name: 'Services', path: '/services' },
        { name: 'Blog', path: '/blog' },
        { name: 'Contact', path: '/contact' },
    ];

    const services = [
        'Bookkeeping',
        'Tax Returns',
        'Payroll Services',
        'VAT Returns',
        'Tax Planning',
        'Company Accounts',
    ];

    return (
        <footer data-testid="main-footer" className="bg-hope-forest text-white relative overflow-hidden">
            {/* Decorative Shape */}
            <div className="absolute top-0 right-0 w-96 h-96 bg-hope-emerald/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
            
            <div className="container-hope relative z-10">
                {/* Main Footer Content */}
                <div className="py-16 lg:py-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
                    {/* Company Info */}
                    <div className="space-y-6">
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-2xl bg-white text-hope-emerald flex items-center justify-center font-heading font-bold text-xl">
                                H
                            </div>
                            <span className="font-heading font-bold text-xl">Hope Accountancy</span>
                        </div>
                        <p className="text-white/70 leading-relaxed">
                            Premium accounting solutions helping UK businesses stay compliant, save time, and grow confidently.
                        </p>
                        <div className="flex gap-4">
                            <a 
                                href="#" 
                                data-testid="social-facebook"
                                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-all"
                            >
                                <Facebook className="w-5 h-5" />
                            </a>
                            <a 
                                href="#" 
                                data-testid="social-twitter"
                                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-all"
                            >
                                <Twitter className="w-5 h-5" />
                            </a>
                            <a 
                                href="#" 
                                data-testid="social-linkedin"
                                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-all"
                            >
                                <Linkedin className="w-5 h-5" />
                            </a>
                            <a 
                                href="#" 
                                data-testid="social-instagram"
                                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-all"
                            >
                                <Instagram className="w-5 h-5" />
                            </a>
                        </div>
                    </div>

                    {/* Quick Links */}
                    <div>
                        <h3 className="font-heading font-semibold text-lg mb-6">Quick Links</h3>
                        <ul className="space-y-4">
                            {quickLinks.map((link) => (
                                <li key={link.path}>
                                    <Link 
                                        to={link.path}
                                        data-testid={`footer-link-${link.name.toLowerCase().replace(' ', '-')}`}
                                        className="text-white/70 hover:text-white transition-colors"
                                    >
                                        {link.name}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Services */}
                    <div>
                        <h3 className="font-heading font-semibold text-lg mb-6">Our Services</h3>
                        <ul className="space-y-4">
                            {services.map((service) => (
                                <li key={service}>
                                    <Link 
                                        to="/services"
                                        className="text-white/70 hover:text-white transition-colors"
                                    >
                                        {service}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Contact Info */}
                    <div>
                        <h3 className="font-heading font-semibold text-lg mb-6">Contact Us</h3>
                        <ul className="space-y-5">
                            <li className="flex items-start gap-4">
                                <div className="w-10 h-10 rounded-xl bg-hope-soft/30 flex items-center justify-center flex-shrink-0">
                                    <Phone className="w-5 h-5" />
                                </div>
                                <div>
                                    <p className="text-sm text-white/50 mb-1">Phone / WhatsApp</p>
                                    <a 
                                        href="tel:07440747050" 
                                        data-testid="footer-phone"
                                        className="text-white hover:text-hope-gold transition-colors"
                                    >
                                        07440 747050
                                    </a>
                                </div>
                            </li>
                            <li className="flex items-start gap-4">
                                <div className="w-10 h-10 rounded-xl bg-hope-soft/30 flex items-center justify-center flex-shrink-0">
                                    <Mail className="w-5 h-5" />
                                </div>
                                <div>
                                    <p className="text-sm text-white/50 mb-1">Email</p>
                                    <a 
                                        href="mailto:info@hopeaccountancy.co.uk" 
                                        data-testid="footer-email"
                                        className="text-white hover:text-hope-gold transition-colors break-all"
                                    >
                                        info@hopeaccountancy.co.uk
                                    </a>
                                </div>
                            </li>
                            <li className="flex items-start gap-4">
                                <div className="w-10 h-10 rounded-xl bg-hope-soft/30 flex items-center justify-center flex-shrink-0">
                                    <MapPin className="w-5 h-5" />
                                </div>
                                <div>
                                    <p className="text-sm text-white/50 mb-1">Address</p>
                                    <p className="text-white/90">
                                        713A Green Lane,<br />
                                        Dagenham, RM8 1UU, UK
                                    </p>
                                </div>
                            </li>
                            <li className="flex items-start gap-4">
                                <div className="w-10 h-10 rounded-xl bg-hope-soft/30 flex items-center justify-center flex-shrink-0">
                                    <Clock className="w-5 h-5" />
                                </div>
                                <div>
                                    <p className="text-sm text-white/50 mb-1">Opening Hours</p>
                                    <p className="text-white/90">
                                        Mon–Fri: 08:00 AM – 07:00 PM
                                    </p>
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Bottom Bar */}
                <div className="border-t border-white/10 py-6">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                        <p className="text-white/50 text-sm text-center md:text-left">
                            © {currentYear} Hope Accountancy. All rights reserved.
                        </p>
                        <div className="flex gap-6">
                            <Link to="/privacy" className="text-white/50 hover:text-white text-sm transition-colors">
                                Privacy Policy
                            </Link>
                            <Link to="/terms" className="text-white/50 hover:text-white text-sm transition-colors">
                                Terms of Service
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
};
